/* The Aesthetic Cove — Christmas gift finder.
   One ARIA tab list, two layouts. From 768px up the tabs are baubles on a fir
   wreath: click one, use the arrow keys, or swipe to turn the wreath, and the
   chosen person settles at the top. Below 768px the same baubles hang in a
   row from a string. Every
   recipient's products are already in the page; this only shows the right panel.
   No wheel or scroll listeners, the page scroll is never touched, and reduced
   motion means no rotation animation, just an instant switch. */

(() => {
  const mobile = window.matchMedia('(max-width: 767px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUS = -90; // the selected label sits at the top of the dial

  document.querySelectorAll('[data-gift-finder]').forEach((section) => {
    if (section.dataset.ready) return;
    section.dataset.ready = '1';

    const $ = (sel) => section.querySelector(sel);
    const dial = $('[data-finder-dial]');
    const rotor = $('[data-finder-rotor]');
    const tabs = rotor ? [...rotor.querySelectorAll('[role="tab"]')] : [];
    const panels = tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
    const hub = $('[data-finder-hub]');
    const hubName = $('[data-finder-name]');
    const hubLine = $('[data-finder-line]');
    const hubGlyph = $('[data-finder-glyph]');
    const counter = $('[data-finder-current]');
    const n = tabs.length;
    if (!dial || !n) return;

    const step = 360 / n;
    let rot = FOCUS; // current rotation of the rotor, degrees
    let index = 0; // the tab marked as selected; `shown` is the panel on screen
    let shown = 0, raf = 0, dragMoved = false;

    const apply = (deg) => {
      rot = deg;
      section.style.setProperty('--rot', `${deg}deg`);
    };
    // Which tab sits nearest the top for a given rotation.
    const indexFor = (deg) => ((Math.round((FOCUS - deg) / step) % n) + n) % n;
    // The rotation that puts tab i at the top, on the turn closest to `near`.
    const rotFor = (i, near = rot) => {
      const base = FOCUS - i * step;
      return base + Math.round((near - base) / 360) * 360;
    };
    const tween = (to) => {
      cancelAnimationFrame(raf);
      if (reduce.matches) return apply(to);
      const from = rot;
      const start = performance.now();
      const frame = (now) => {
        const p = Math.min(1, (now - start) / 950);
        apply(from + (to - from) * (p === 1 ? 1 : 1 - Math.pow(2, -10 * p))); // expo out
        if (p < 1) raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    };
    const restart = (el, cls) => {
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    };

    // Mark tab i as current: tabs, hub text and counter. Panels are left alone so
    // a drag can pass over labels without flashing product sets.
    const mark = (i) => {
      index = i;
      tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', String(k === i));
        t.tabIndex = k === i ? 0 : -1;
      });
      const t = tabs[i];
      if (hubName) hubName.textContent = t.dataset.label || '';
      if (hubLine) hubLine.textContent = t.dataset.line || '';
      if (hubGlyph) hubGlyph.textContent = t.dataset.glyph || hubGlyph.dataset.default || '';
      if (counter) counter.textContent = String(i + 1).padStart(2, '0');
      if (hub && !reduce.matches) restart(hub, 'is-swap');
    };
    // Chips: bring the chosen one into view by scrolling the row only.
    const centreChip = (tab) => {
      const left = tab.offsetLeft - (rotor.clientWidth - tab.offsetWidth) / 2;
      rotor.scrollTo({ left, behavior: reduce.matches ? 'auto' : 'smooth' });
    };
    const select = (i, { focus = false, near = rot } = {}) => {
      i = ((i % n) + n) % n;
      const changed = i !== shown;
      shown = i;
      mark(i);
      panels.forEach((p, k) => {
        if (!p) return;
        p.hidden = k !== i;
        if (k === i && changed && !reduce.matches) restart(p, 'is-entering');
      });
      if (focus) tabs[i].focus({ preventScroll: true });
      if (mobile.matches) centreChip(tabs[i]);
      else tween(rotFor(i, near));
    };

    /* ---------- click, buttons, keyboard ---------- */
    tabs.forEach((t, i) => t.addEventListener('click', () => !dragMoved && select(i)));
    $('[data-finder-prev]')?.addEventListener('click', () => select(index - 1));
    $('[data-finder-next]')?.addEventListener('click', () => select(index + 1));
    rotor.addEventListener('keydown', (e) => {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      let to = null;
      if (e.key in keys) to = index + keys[e.key];
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = n - 1;
      if (to === null) return;
      e.preventDefault();
      select(to, { focus: true });
    });

    /* ---------- swipe or drag to turn (dial layout only) ---------- */
    let dragging = false, pointerId = null, velocity = 0;
    let startAngle = 0, startRot = 0, lastAngle = 0, lastTime = 0;
    const angleAt = (e) => {
      const r = dial.getBoundingClientRect();
      return (Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180) / Math.PI;
    };
    const unwrap = (a, ref) => {
      let d = a - ref;
      while (d > 180) d -= 360;
      while (d < -180) d += 360;
      return ref + d;
    };
    const capture = (on) => {
      try {
        if (on) dial.setPointerCapture(pointerId);
        else dial.releasePointerCapture(pointerId);
      } catch (err) {
        /* pointer already gone */
      }
    };

    dial.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || mobile.matches) return;
      cancelAnimationFrame(raf);
      dragging = true;
      dragMoved = false;
      pointerId = e.pointerId;
      startAngle = lastAngle = angleAt(e);
      startRot = rot;
      lastTime = performance.now();
      velocity = 0;
    });
    dial.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const a = unwrap(angleAt(e), lastAngle);
      const now = performance.now();
      velocity = (a - lastAngle) / Math.max(16, now - lastTime);
      lastAngle = a;
      lastTime = now;
      const delta = a - startAngle;
      if (!dragMoved && Math.abs(delta) > 3) {
        // Capture only once it is a real turn, so plain clicks still reach the label.
        dragMoved = true;
        dial.classList.add('is-dragging');
        capture(true);
      }
      if (!dragMoved) return;
      apply(startRot + delta);
      const i = indexFor(rot);
      if (i !== index) mark(i);
    });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      dial.classList.remove('is-dragging');
      capture(false);
      if (!dragMoved) return;
      const projected = rot + velocity * 260; // a little momentum, then settle
      select(indexFor(projected), { near: projected });
      setTimeout(() => (dragMoved = false), 50);
    };
    dial.addEventListener('pointerup', end);
    dial.addEventListener('pointercancel', end);

    /* ---------- a gentle turn the first time the dial scrolls into view ---------- */
    if (!reduce.matches && !mobile.matches && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          if (dragging || mobile.matches) return;
          apply(rot + step * 0.9);
          tween(rotFor(index));
        },
        { threshold: 0.35 }
      );
      io.observe(dial);
    }

    // The wreath's fairy lights twinkle only while the band is on screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => section.classList.toggle('is-visible', entry.isIntersecting)).observe(section);
    } else {
      section.classList.add('is-visible');
    }

    mobile.addEventListener('change', () => (mobile.matches ? centreChip(tabs[index]) : apply(rotFor(index))));
    apply(rotFor(0));
    mark(0);
  });
})();
